// Polish Phase 1 (AUDIO-01/02/06 / PERF-04) — the procedural Web Audio engine.
//
// A single lazily-created AudioContext drives a small fixed mixer graph:
//
//   cue voices ─┐
//               ├─> sfxBus(gain) ──────────────┐
//   (dry)       │                              ├─> master(gain) ─> limiter ─> out
//               └─> spaceWet(gain) ─> delay ───┘            (DynamicsCompressor
//                          ^___ lp <─ feedback <┘             as a soft limiter)
//
//   ambient/duck:  musicBus(gain) ─> master   (the bus + duck()/setMusicGain()
//                                               ramps ship now; nothing drives
//                                               them yet — the ambient source
//                                               and big-moment ducking are
//                                               future work, AUD-4.)
//
// The mixer nodes (master/limiter/sfxBus/space/musicBus) are created ONCE and
// live for the engine's lifetime — they are the console, not per-cue voices.
// Per-cue oscillators/noise are one-shot and self-disconnect (synth.ts), so the
// graph never accumulates dead nodes (PERF-04).
//
// AUTOPLAY (AUDIO-02): browsers start an AudioContext suspended until a user
// gesture. `unlock()` resumes it and is idempotent; the React binding
// (useAudio.ts) attaches it to the first pointerdown/keydown/touchstart.
//
// REDUCED MOTION: not consulted here — see stores/audio.ts for the codified
// policy. Playback is gated on mute (targetGain <= 0) only.
//
// TESTABILITY: the AudioContext is obtained via an injectable factory so
// happy-dom tests (no real AudioContext) inject a mock and assert graph
// construction / mute ramps / cue dispatch without a real audio device.

import { CUES, type CueOptions, type SfxCue } from './cues'
import { type AudioThemeProfile, DEFAULT_AUDIO_PROFILE } from './theme-audio'

/** Subset of AudioContext the engine constructs its mixer from. The real
 * AudioContext satisfies this; tests provide a structural mock. */
export type EngineAudioContext = AudioContext

export type AudioContextFactory = () => EngineAudioContext | null

function defaultFactory(): EngineAudioContext | null {
  if (typeof window === 'undefined') return null
  const Ctor =
    (window as unknown as { AudioContext?: typeof AudioContext })
      .AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext
  if (!Ctor) return null
  try {
    return new Ctor()
  } catch {
    return null
  }
}

interface Mixer {
  readonly master: GainNode
  readonly sfxBus: GainNode
  readonly musicBus: GainNode
  readonly spaceWet: GainNode
  readonly delay: DelayNode
  readonly feedback: GainNode
  readonly spaceLp: BiquadFilterNode
}

export class AudioEngine {
  private readonly factory: AudioContextFactory
  private ctx: EngineAudioContext | null = null
  private mixer: Mixer | null = null
  private profile: AudioThemeProfile = DEFAULT_AUDIO_PROFILE
  private targetGain = 0.8
  private musicGain = 0
  private unlocked = false

  constructor(factory: AudioContextFactory = defaultFactory) {
    this.factory = factory
  }

  /** True once a context exists and has been resumed by a user gesture. */
  get isUnlocked(): boolean {
    return this.unlocked
  }

  /** Test/inspection seam — the live context (or null if not yet created). */
  get context(): EngineAudioContext | null {
    return this.ctx
  }

  private ensureContext(): EngineAudioContext | null {
    if (this.ctx) return this.ctx
    const ctx = this.factory()
    if (!ctx) return null
    this.ctx = ctx
    this.buildGraph(ctx)
    return ctx
  }

  private buildGraph(ctx: EngineAudioContext): void {
    const master = ctx.createGain()
    master.gain.value = 1
    // Soft limiter on the master bus so layered big moments never clip.
    const limiter = ctx.createDynamicsCompressor()
    limiter.threshold.value = -6
    limiter.knee.value = 6
    limiter.ratio.value = 12
    limiter.attack.value = 0.003
    limiter.release.value = 0.12
    master.connect(limiter)
    limiter.connect(ctx.destination)

    const sfxBus = ctx.createGain()
    sfxBus.gain.value = this.targetGain
    sfxBus.connect(master)

    const musicBus = ctx.createGain()
    musicBus.gain.value = this.musicGain
    musicBus.connect(master)

    // Feedback-delay "space" reverb send, tuned per theme.
    const spaceWet = ctx.createGain()
    const delay = ctx.createDelay(1)
    const feedback = ctx.createGain()
    const spaceLp = ctx.createBiquadFilter()
    spaceLp.type = 'lowpass'
    spaceLp.frequency.value = 2600
    sfxBus.connect(spaceWet)
    spaceWet.connect(delay)
    delay.connect(master) // wet out
    delay.connect(spaceLp)
    spaceLp.connect(feedback)
    feedback.connect(delay) // decaying tail

    this.mixer = {
      master,
      sfxBus,
      musicBus,
      spaceWet,
      delay,
      feedback,
      spaceLp,
    }
    this.applyProfileToGraph()
  }

  private applyProfileToGraph(): void {
    const m = this.mixer
    const ctx = this.ctx
    if (!m || !ctx) return
    const now = ctx.currentTime
    m.spaceWet.gain.setValueAtTime(this.profile.spaceWet, now)
    m.delay.delayTime.setValueAtTime(this.profile.spaceDelay, now)
    m.feedback.gain.setValueAtTime(this.profile.spaceFeedback, now)
  }

  /** Resume the context on a user gesture. Idempotent; safe to call before a
   * context exists (creates one) and repeatedly. */
  unlock(): void {
    const ctx = this.ensureContext()
    if (!ctx) return
    const maybeResume = (ctx as { resume?: () => Promise<void> }).resume
    if (typeof maybeResume === 'function' && ctx.state === 'suspended') {
      void maybeResume.call(ctx).catch(() => {
        /* gesture not trusted yet — will retry on next gesture */
      })
    }
    if (ctx.state === 'running' || ctx.state === undefined) this.unlocked = true
    else {
      // Optimistically mark unlocked; resume() resolves async. If it fails the
      // next gesture re-runs this.
      this.unlocked = true
    }
  }

  /** Set the active theme's audio profile (timbre + reverb). */
  setProfile(profile: AudioThemeProfile): void {
    this.profile = profile
    this.applyProfileToGraph()
  }

  /** Set the SFX bus linear gain [0,1]. 0 = muted (play() then no-ops). */
  setSfxGain(gain: number): void {
    this.targetGain = Math.max(0, Math.min(1, gain))
    const m = this.mixer
    const ctx = this.ctx
    if (!m || !ctx) return
    const now = ctx.currentTime
    m.sfxBus.gain.cancelScheduledValues(now)
    m.sfxBus.gain.setValueAtTime(m.sfxBus.gain.value, now)
    m.sfxBus.gain.linearRampToValueAtTime(this.targetGain, now + 0.05)
  }

  /** Set the music/ambient bus gain [0,1]. */
  setMusicGain(gain: number): void {
    this.musicGain = Math.max(0, Math.min(1, gain))
    const m = this.mixer
    const ctx = this.ctx
    if (!m || !ctx) return
    const now = ctx.currentTime
    m.musicBus.gain.cancelScheduledValues(now)
    // AUD-1: anchor the ramp's start at the current value. Without this, a
    // stale scheduled event surviving cancelScheduledValues can make the ramp
    // start jump (an audible click) the moment an ambient bed is wired.
    // Mirrors setSfxGain (above) and duck (below).
    m.musicBus.gain.setValueAtTime(m.musicBus.gain.value, now)
    m.musicBus.gain.linearRampToValueAtTime(this.musicGain, now + 0.05)
  }

  /** Duck the music/ambient bus for a big moment, then restore. No-op when no
   * ambient is playing, but the bus + ramp are real (AUDIO-06). */
  duck(durationMs = 800, amount = 0.4): void {
    const m = this.mixer
    const ctx = this.ctx
    if (!m || !ctx) return
    const now = ctx.currentTime
    const ducked = this.musicGain * (1 - Math.max(0, Math.min(1, amount)))
    m.musicBus.gain.cancelScheduledValues(now)
    m.musicBus.gain.setValueAtTime(m.musicBus.gain.value, now)
    m.musicBus.gain.linearRampToValueAtTime(ducked, now + 0.05)
    m.musicBus.gain.linearRampToValueAtTime(
      this.musicGain,
      now + durationMs / 1000,
    )
  }

  /** Play a cue. No-op when muted (targetGain<=0) or no audio is available. */
  play(cue: SfxCue, opts?: CueOptions): void {
    if (this.targetGain <= 0) return
    const ctx = this.ensureContext()
    if (!ctx) return
    const m = this.mixer
    if (!m) return
    const fn = CUES[cue]
    if (!fn) return
    try {
      fn(ctx, m.sfxBus, this.profile, opts ?? {})
    } catch {
      // A malformed schedule must never break gameplay — swallow.
    }
  }

  /** Tear down the mixer + close the context (route unmount / app teardown). */
  dispose(): void {
    const m = this.mixer
    if (m) {
      try {
        m.master.disconnect()
        m.sfxBus.disconnect()
        m.musicBus.disconnect()
        m.spaceWet.disconnect()
        m.delay.disconnect()
        m.feedback.disconnect()
        m.spaceLp.disconnect()
      } catch {
        /* ignore */
      }
    }
    const ctx = this.ctx
    if (
      ctx &&
      typeof (ctx as { close?: () => Promise<void> }).close === 'function'
    ) {
      void (ctx as { close: () => Promise<void> }).close().catch(() => {})
    }
    this.mixer = null
    this.ctx = null
    this.unlocked = false
  }
}

/** App-wide engine singleton. */
export const audioEngine = new AudioEngine()

/** Convenience: play a cue on the singleton. The one import call-sites need. */
export function playSfx(cue: SfxCue, opts?: CueOptions): void {
  audioEngine.play(cue, opts)
}
